import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { withLocalize } from "react-localize-redux";

import {
	SearchBackend,
	EvidenceBackend,
	OptimizedMixPanel,
	UserUtils,
} from "wallet-elementkit/dist/services";
import {
	DetailCost as DetailCostEnum,
	DetailLevel as DetailLevelEnum,
	StorageKeys,
} from "wallet-elementkit/dist/constants/constants";
import { selectUser } from "../../store/features/user";
import { useAppSelector } from "../../store/hooks";
import "../../styles/credential/credential.scss";
import CredentialWeb from "./CredentialWeb";
import CredentialMobile from "./CredentialMobile";
import mixpanel from "mixpanel-browser";
import {
	DetailCost,
	DetailDuration,
	DetailDurationType,
	DetailLevel,
	DetailType,
} from "wallet-elementkit/dist/translations";
import { getItem, storeItem } from "../../backend/StorageUtils";
const NotFound = React.lazy(() => import("../../components/common/NotFound"));

function Credential(props) {
	const userMeta = useAppSelector(selectUser);
	const location = useLocation();
	const detailTypes = DetailType;
	const detailCost = DetailCost;
	const detailDuration = DetailDuration;
	const detailLevel = DetailLevel;
	const detailDurationType = DetailDurationType;
	const credNo = (props.match && props.match.params.credno) || "";
	const { campaignId = "" } = useParams();
	const deliveryId = (props.match && props.match.params.deliveryId) || "";
	const [loading, setLoading] = useState(true);
	const [imageLoading, setImageLoading] = useState(false);
	const [error, setError] = useState("");

	const [campaign, setCampaign] = useState();
	const [campaignDetails, setCampaignDetails] = useState();
	const [type, setType] = useState("");
	const [level, setLevel] = useState("");
	const [duration, setDuration] = useState("");
	const [cost, setCost] = useState("");
	const [courseRating, setCourseRating] = useState(0.0);
	const [courseRatingCount, setCourseRatingCount] = useState(0);
	const [attendeeCount, setAttendeeCount] = useState(0);
	const [earningCriteriaHTML, setEarningCriteriaHTML] = useState("");
	const [issuerEmail, setIssuerEmail] = useState("");
	const [evidences, setEvidences] = useState([]);
	const [
		supportingDocumentationsWithMeta,
		setSupportingDocumentationsWithMeta,
	] = useState([]);
	const [searchCredentialResp, setSearchCredentialResp] = useState();
	const [skills, setSkills] = useState([]);

	const optimizedMixpanel = new OptimizedMixPanel(mixpanel);

	async function fetchSupportingDocumentationLinks(certificationNo) {
		const supportingDocumentationLinks =
			await EvidenceBackend.parseSupportingDocumentationLinks(
				certificationNo || credNo
			);

		setSupportingDocumentationsWithMeta(supportingDocumentationLinks);
	}

	useEffect(() => {
		optimizeDetailsForLanguage();
	}, [props.activeLanguage, searchCredentialResp]);

	function optimizeDetailsForLanguage() {
		if (searchCredentialResp && props.activeLanguage) {
			const langCode =
				props.activeLanguage?.code ??
				process.env.REACT_APP_DEFAULT_LANGUAGE ??
				"en";
			let resp = searchCredentialResp;
			setType(detailTypes[langCode][resp.campaign.detail.type]);
			setLevel(
				resp.campaign.detail.level === DetailLevelEnum.NA
					? ""
					: detailLevel[langCode][resp.campaign.detail.level]
			);
			setDuration(
				resp.campaign.detail.duration === 5
					? ""
					: resp.campaign.detail.duration === 4
					? `${resp.campaign.detail.durationNumber} ${
							resp.campaign.detail.durationNumber > 1 &&
							props.activeLanguage?.code === "en"
								? detailDurationType[langCode][
										resp.campaign.detail.durationType - 1
								  ] + "s"
								: detailDurationType[langCode][
										resp.campaign.detail.durationType - 1
								  ]
					  }`
					: detailDuration[langCode][resp.campaign.detail.duration]
			);
			setCost(
				resp.campaign.detail.cost === DetailCostEnum.NA
					? ""
					: detailCost[langCode][resp.campaign.detail.cost]
			);

			setCourseRating(
				resp.campaign.detail.courseRating
					? resp.campaign.detail.courseRating
					: 0.0
			);
			setCourseRatingCount(
				resp.campaign.detail.courseRatingCount
					? resp.campaign.detail.courseRatingCount
					: 0
			);
			setAttendeeCount(
				resp.campaign.detail.attendeeCount
					? resp.campaign.detail.attendeeCount
					: 0
			);
			setEarningCriteriaHTML(resp.campaign.detail.earningCriteriaHTML || "");
		}
	}

	useEffect(() => {
		const search = location.search;
		const canvas = new URLSearchParams(search).get("canvas") === "true";

		if (canvas) {
			// Handle loop for canvas until credential is created
			let reqCount = 100; // Max number of requests
			let searchRes;
			let intervalId = setInterval(async () => {
				if (reqCount === 0 || searchRes) {
					clearInterval(intervalId);
					return;
				}
				await handleSearch({
					canvas: true,
				})
					.then((res) => (searchRes = res))
					.catch((e) => (searchRes = false));
				reqCount--;
			}, 4000);
			return () => clearInterval(intervalId);
		} else {
			setParsedEvidences();
			(async function fetchSearchData() {
				await handleSearch();
			})();
		}
	}, [credNo]);

	const setParsedEvidences = () => {
		// Did not use await because of the unexpected setState behaviour. (discuss anil)
		if (props.isPreview) {
			EvidenceBackend.parsePreviewEvidences(deliveryId).then((result) => {
				setEvidences(result);
			});
		} else {
			EvidenceBackend.parseEvidences(credNo).then((result) => {
				setEvidences(result);
			});
		}
	};

	const handleSearch = async (options = { canvas: false }) => {
		setLoading(true);
		let resp;
		let id = getItem(StorageKeys.user_id);
		let viewerId = null;
		if (!id) {
			viewerId = UserUtils.genereateViewerId();
			storeItem(StorageKeys.user_id, viewerId);
		} else {
			viewerId = id;
		}

		const [searchResult, supportingDocs] = await Promise.all([
			SearchBackend.getCampaign(campaignId),
			fetchSupportingDocumentationLinks(),
		]);
		resp = searchResult;

		if (!resp.success) {
			if (options.canvas) {
				return false;
			}
			setError("notFound2");
			setLoading(false);
			setSearchCredentialResp(undefined);
			return false;
		}

		const langCode =
			props.activeLanguage?.code ??
			process.env.REACT_APP_DEFAULT_LANGUAGE ??
			"en";

		setCampaign(resp.campaign);
		setCampaignDetails(resp.campaign.detail);
		setSkills(resp.campaign.detail.skills);
		setType(detailTypes[langCode][resp.campaign.detail.type]);
		setLevel(
			resp.campaign.detail.level === DetailLevelEnum.NA
				? ""
				: detailLevel[langCode][resp.campaign.detail.level]
		);
		setDuration(
			resp.campaign.detail.duration === 5
				? ""
				: resp.campaign.detail.duration === 4
				? `${resp.campaign.detail.durationNumber} ${
						resp.campaign.detail.durationNumber > 1 &&
						props.activeLanguage?.code === "en"
							? detailDurationType[langCode][
									resp.campaign.detail.durationType - 1
							  ] + "s"
							: detailDurationType[langCode][
									resp.campaign.detail.durationType - 1
							  ]
				  }`
				: detailDuration[langCode][resp.campaign.detail.duration]
		);
		setCost(
			resp.campaign.detail.cost === DetailCostEnum.NA
				? ""
				: detailCost[langCode][resp.campaign.detail.cost]
		);
		setCourseRating(
			resp.campaign.detail.courseRating
				? resp.campaign.detail.courseRating
				: 0.0
		);
		setCourseRatingCount(
			resp.campaign.detail.courseRatingCount
				? resp.campaign.detail.courseRatingCount
				: 0
		);
		setAttendeeCount(
			resp.campaign.detail.attendeeCount
				? resp.campaign.detail.attendeeCount
				: 0
		);
		setEarningCriteriaHTML(resp.campaign.detail.earningCriteriaHTML || "");
		setError("");
		setLoading(false);
		setSearchCredentialResp(resp);
		optimizedMixpanel.track("Campaign View", {
			campaign_id: credNo,
			isLogged: userMeta.logged,
		});
		return true;
	};

	const handleUncreatedImage = async () => {
		setImageLoading(true);
		setTimeout(() => {
			setImageLoading(false);
		}, 10000);
	};

	const handleMailTo = async (type) => {
		if (props.activeLanguage) {
			if (type === "reportNotFound")
				if (props.activeLanguage.code === "tr")
					window.open(
						`mailto:${process.env.REACT_APP_BASE_MAIL_TO}.com?subject=Sertifika Bulunamadı Sorunu sertifika numarası: ${credNo}&body=Merhaba, %0D%0A%0D%0A${credNo} seri numaralı sertifikamla alakalı sorunumu sizinle aşağıda paylaşıyorum.%0D%0A%0D%0ASevgiler,`
					);
				else
					window.open(
						`mailto:${process.env.REACT_APP_BASE_MAIL_TO}.com?subject=Certificate Not Found Issue with id ${credNo}&body=Hello, %0D%0AI'm sharing with you my issue with the ${credNo} serial numbered credential.%0D%0A%0D%0ARegards,`
					);
			if (type === "deletedCertificate")
				if (props.activeLanguage.code === "tr")
					window.open(
						`mailto:${issuerEmail}?subject=Silinmiş Sertifika, Sertifika numarası: ${credNo}&body=Merhaba, %0D%0A%0D%0A${credNo} seri numaralı sertifikamla alakalı sorunumu sizinle aşağıda paylaşıyorum.%0D%0A%0D%0ASevgiler,`
					);
				else
					window.open(
						`mailto:${issuerEmail}?subject=Deleted Certificate with id: ${credNo}&body=Hello, %0D%0AI'm sharing with you my issue with the ${credNo} serial numbered credential.%0D%0A%0D%0ARegards,`
					);
		}
	};

	if (!error) {
		if (props.isMobile) {
			return (
				<div id="verify-container-mobile">
					<CredentialMobile
						campaign={campaign}
						loading={loading}
						imageLoading={imageLoading}
						issuerEmail={issuerEmail}
						skills={skills}
						type={type}
						level={level}
						duration={duration}
						cost={cost}
						credNo={credNo}
						courseRating={courseRating}
						attendeeCount={attendeeCount}
						earningCriteriaHTML={earningCriteriaHTML}
						courseRatingCount={courseRatingCount}
						evidences={evidences}
						campaignDetails={campaignDetails}
						logged={userMeta.logged}
						handleUncreatedImage={handleUncreatedImage}
						supportingDocumentationsWithMeta={supportingDocumentationsWithMeta}
					/>
				</div>
			);
		} else {
			return (
				<div id="verify-container-web">
					<CredentialWeb
						campaign={campaign}
						loading={loading}
						imageLoading={imageLoading}
						issuerEmail={issuerEmail}
						skills={skills}
						type={type}
						level={level}
						duration={duration}
						cost={cost}
						credNo={credNo}
						courseRating={courseRating}
						attendeeCount={attendeeCount}
						earningCriteriaHTML={earningCriteriaHTML}
						courseRatingCount={courseRatingCount}
						evidences={evidences}
						campaignDetails={campaignDetails}
						logged={userMeta.logged}
						handleUncreatedImage={handleUncreatedImage}
						supportingDocumentationsWithMeta={supportingDocumentationsWithMeta}
					/>
				</div>
			);
		}
	} else {
		return (
			<NotFound
				isMobile={props.isMobile}
				error={error}
				handleMailTo={handleMailTo}
			/>
		);
	}
}

export default withLocalize(Credential);
